import { Injectable } from "@angular/core";
import { TaxonomyStatus } from '../enums/taxonomy-status.enum';
import { LoggerService } from '../services/logger.service';


@Injectable({ providedIn: 'root' })
export class AppHelper {
  //#region Fields

  private static readonly defaultLocale = 'en-US';

  //#endregion

  //#region C-tor

  constructor(
    private logger: LoggerService) { }

  //#endregion

  //#region Array

  includes<T>(array: T[], item: T): boolean {
    if (array) {
      for (const arrItem of array) {
        if (arrItem === item) {
          return true;
        }
      }
    }

    return false;
  }

  find<T>(array: T[], predicate: (item: T) => boolean): T {
    if (array) {
      for (const arrItem of array) {
        if (predicate(arrItem)) {
          return arrItem;
        }
      }
    }

    return undefined;
  }

  findIndex<T>(array: T[], predicate: (item: T) => boolean): number {
    if (array) {
      let counter = 0;

      for (const arrItem of array) {
        if (predicate(arrItem)) {
          return counter;
        }

        counter++;
      }
    }

    return -1;
  }

  //#endregion

  //#region Other

  getNewGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);

      return v.toString(16);
    });
  }

  processExcelError(error: any): void {
    this.logger.error(error);

    if (error instanceof OfficeExtension.Error) {
      this.logger.debug(error.debugInfo);
    }
  }

  getErrorMessage(error: any): string {
    if (error.error && error.error.Message) {
      return error.statusText
        ? `${error.statusText} (${error.status}): ${error.error.Message}`
        : error.error.Message;
    }

    if (error.statusText) {
      return `${error.statusText} (${error.status})`;
    }

    if (error.message) {
      this.logger.error(error);
      return error.message;
    }

    if (error.error) {
      return error.error;
    }

    return error;
  }

  getStatusString(status: TaxonomyStatus): string {
    const chars = Array.from(TaxonomyStatus[status]);

    for (let i = 1; i < chars.length; i++) {
      const char = chars[i];

      if (char === char.toUpperCase()) {
        chars.splice(i++, 0, ' ');
      }
    }

    return chars.join('');
  }

  getStatus(statusString: string): TaxonomyStatus {
    return TaxonomyStatus[statusString.split(' ').join('')];
  }

  formatDate(date: Date, excelFormat: string): string {
    return excelFormat.replace(/(m{1,5}|d{1,4}|y{4}|y{2})/g, chunk => {
      switch (chunk) {
        case 'd':
          return date.getUTCDate().toString();

        case 'dd':
          const day = date.getUTCDate();
          return day < 10 ? '0' + day : day.toString();

        case 'ddd':
          return date.toLocaleDateString(AppHelper.defaultLocale, { weekday: 'short', timeZone: 'Etc/UTC' });

        case 'dddd':
          return date.toLocaleDateString(AppHelper.defaultLocale, { weekday: 'long', timeZone: 'Etc/UTC' });

        case 'm':
          return (date.getUTCMonth() + 1).toString();

        case 'mm':
          const month = date.getUTCMonth() + 1;
          return month < 10 ? '0' + month : month.toString();

        case 'mmm':
          return date.toLocaleDateString(AppHelper.defaultLocale, { month: 'short', timeZone: 'Etc/UTC' });

        case 'mmmm':
          return date.toLocaleDateString(AppHelper.defaultLocale, { month: 'long', timeZone: 'Etc/UTC' });

        case 'mmmmm':
          return date.toLocaleDateString(AppHelper.defaultLocale, { month: 'short', timeZone: 'Etc/UTC' }).substring(0, 1);

        case 'yy':
          return date.toLocaleDateString(AppHelper.defaultLocale, { year: '2-digit', timeZone: 'Etc/UTC' });

        case 'yyyy':
          return date.toLocaleDateString(AppHelper.defaultLocale, { year: 'numeric', timeZone: 'Etc/UTC' });

        default:
          return '';
      }
    });
  }

  //#endregion
}
